// src/components/features/ProductImprovementHub.js
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  Search, RefreshCw, ArrowDown, Star, Tag, Calendar, X,
  SlidersHorizontal, Lightbulb, Award, Target,
  AlertCircle, ChevronDown,
  ClipboardList, Loader2, DatabaseZap
} from 'lucide-react';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db, functionsInstance } from '../../firebase/firebase';
import { httpsCallable } from 'firebase/functions';
import { Chart } from 'chart.js/auto';
import Select from 'react-select';

// --- Local Imports ---
import {
  SEASON_CANONICALS, SEASON_ALIASES, SUBJECT_ALIASES, TYPE_ALIASES,
  gradeTokens, GRADE_OPTIONS, UPCOMING_DATES, SUBJECT_AREAS
} from './ProductImprovementHub/ProductHubConstants';
import Headliners from './ProductImprovementHub/ProductHubHeadliners';
import ProductRow from './ProductImprovementHub/ProductHubRow';
import CoachPanel from './ProductImprovementHub/ProductHubCoachPanel';
// --- End Local Imports ---

import { runAudit } from '../../utils/audit';

/* ========================================================== */
/* =================== Graph Modal (Local) ================== */
/* ========================================================== */
const HubRankHistoryModal = ({ isOpen, onClose, historyData, keyword, isLoading }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [range, setRange] = useState('all');

  const { labels, data, best } = useMemo(() => {
    const hist = Array.isArray(historyData) ? historyData : [];
    if (hist.length === 0) return { labels: [], data: [], best: null };
    let cutoff = 0; const now = Date.now();
    if (range === '7d') cutoff = now - 7 * 864e5;
    else if (range === '30d') cutoff = now - 30 * 864e5;
    else if (range === '90d') cutoff = now - 90 * 864e5;
    else if (range === '365d') cutoff = now - 365 * 864e5;
    const filtered = range === 'all' ? hist : hist.filter(h => h.date >= cutoff);
    const daily = new Map();
    filtered.forEach(h => {
      if (typeof h.date === 'number' && h.rank != null) {
        const key = new Date(h.date).toISOString().split('T')[0];
        daily.set(key, h.rank);
      }
    });
    const lbls = Array.from(daily.keys())
      .map(d => new Date(`${d}T00:00:00Z`)
      .toLocaleDateString(undefined, { timeZone: 'UTC', month: 'short', day: 'numeric' }));
    const points = Array.from(daily.values());
    let bestPoint = null;
    hist.forEach(h => { if (h.rank != null) { if (!bestPoint || h.rank < bestPoint.rank) bestPoint = h; } });
    return { labels: lbls, data: points, best: bestPoint };
  }, [historyData, range]);

  useEffect(() => {
    if (!isOpen || !chartRef.current || isLoading) return;
    try {
      if (chartInstance.current) chartInstance.current.destroy();
      const ctx = chartRef.current.getContext('2d');
      chartInstance.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: 'Rank',
            data,
            borderColor: '#0ea5e9',
            backgroundColor: 'rgba(14, 165, 233, 0.1)',
            fill: true,
            tension: 0.3,
            pointRadius: 0,
            pointHoverRadius: 3,
            pointHitRadius: 8
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'nearest', intersect: false },
          elements: { point: { radius: 0, hoverRadius: 3, hitRadius: 8 } },
          plugins: { legend: { display: false }, decimation: { enabled: true, algorithm: 'lttb', samples: 300 } },
          scales: {
            y: {
              reverse: true,
              beginAtZero: false,
              grid: { color: '#e2e8f0' },
              ticks: { color: '#64748b', stepSize: 1, callback: v => (Math.floor(v) === v ? v : undefined) }
            },
            x: { grid: { display: false }, ticks: { color: '#64748b', maxRotation: 0, minRotation: 0 } }
          }
        }
      });
    } catch {}
    return () => { if (chartInstance.current) chartInstance.current.destroy(); };
  }, [isOpen, labels, data, isLoading]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-slate-800">Rank History for "{keyword}"</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-slate-100" aria-label="Close rank history">
            <X size={20} className="text-slate-500" />
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          {[{ k: '7d', label: 'Last 7d' }, { k: '30d', label: 'Month' }, { k: '90d', label: 'Quarter' }, { k: '365d', label: 'Year' }, { k: 'all', label: 'All time' }].map(btn => (
            <button
              key={btn.k}
              onClick={() => setRange(btn.k)}
              className={`text-xs px-2 py-1 rounded-md border ${
                range === btn.k ? 'bg-sky-600 border-sky-600 text-white' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
              }`}
            >
              {btn.label}
            </button>
          ))}
        </div>

        <div className="h-72">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 size={32} className="animate-spin text-sky-600" />
              <p className="ml-3 text-slate-500">Loading graph data...</p>
            </div>
          ) : (
            <canvas ref={chartRef}></canvas>
          )}
        </div>

        {best && !isLoading && (
          <div className="mt-4 text-xs text-slate-600">
            <span className="font-semibold text-sky-700">Best rank: </span>
            <span className="font-bold text-sky-700">#{best.rank}</span>{' '}
            on {new Date(best.date).toLocaleDateString()}
          </div>
        )}
      </div>
    </div>
  );
};


/* ========================================================== */
/* =================== Detection & Data Helpers ============= */
/* ========================================================== */

const normalize = (str) => (str || '').toLowerCase().trim();
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&');

const detectInitialAliases = (text, aliasList) => {
  const found = new Set();
  aliasList.forEach(({ canonical, aliases }) => {
    if (!aliases || typeof aliases.forEach !== 'function') return;
    for (const alias of aliases) {
      const regex = new RegExp(`\\b${escapeRegExp(alias)}\\b`, 'i');
      if (regex.test(text)) found.add(canonical);
    }
  });
  return Array.from(found);
};

const mapScrapedData = (scrapedItems, aliasList) => {
  const areas = new Set();
  const specifics = new Set();
  if (!scrapedItems || scrapedItems.length === 0) return { areas: [], specifics: [] };
  const lowercasedItems = scrapedItems.map(item => item.toLowerCase());

  for (const item of lowercasedItems) {
    for (const entry of aliasList) {
      if (!entry.aliases || typeof entry.aliases.some !== 'function') continue;
      const hasMatch = entry.aliases.some(alias => alias.toLowerCase() === item);
      if (hasMatch) {
        if (entry.subjectArea) {
          areas.add(entry.subjectArea);
          if (entry.canonical.toLowerCase() !== entry.subjectArea.toLowerCase()) specifics.add(entry.canonical);
        } else {
          specifics.add(entry.canonical);
        }
        break;
      }
    }
  }
  return { areas: Array.from(areas), specifics: Array.from(specifics) };
};

const detectSeasonsFromProduct = (product) => {
  const title = product?.productTitle || '';
  const description = product?.descriptionText || product?.descriptionHtml || product?.description || '';
  const combined = normalize(`${title} ${description}`);
  return detectInitialAliases(combined, SEASON_ALIASES);
};

const detectSubjectsFromProduct = (product) => {
  const title = product?.productTitle || '';
  const description = product?.descriptionText || product?.descriptionHtml || product?.description || '';
  const rawText = `${title} ${description}`;

  const potentialCanonicals = detectInitialAliases(rawText, SUBJECT_ALIASES);
  if (potentialCanonicals.length === 0) return { areas: [], specifics: [] };

  const counts = {};
  potentialCanonicals.forEach(canonical => {
    counts[canonical] = 0;
    const subjectEntry = SUBJECT_ALIASES.find(s => s.canonical === canonical);
    if (subjectEntry) {
      subjectEntry.aliases.forEach(alias => {
        const regex = new RegExp(`\\b${escapeRegExp(alias)}\\b`, 'gi');
        const matches = rawText.match(regex);
        if (matches) counts[canonical] += matches.length;
      });
    }
  });

  const sortedCanonicals = potentialCanonicals.sort((a, b) => counts[b] - counts[a]);
  const topCanonicals = sortedCanonicals.slice(0, 2);

  const areas = new Set();
  const specifics = new Set();
  topCanonicals.forEach(canonical => {
    const entry = SUBJECT_ALIASES.find(s => s.canonical === canonical);
    if (entry) {
      areas.add(entry.subjectArea);
      if (entry.canonical.toLowerCase() !== entry.subjectArea.toLowerCase()) specifics.add(entry.canonical);
    }
  });

  return { areas: Array.from(areas), specifics: Array.from(specifics) };
};

const detectTypesFromProduct = (product) => {
  const title = product?.productTitle || '';
  const description = product?.descriptionText || product?.descriptionHtml || product?.description || '';
  const rawText = `${title} ${description}`;
  const detectedCanonicals = detectInitialAliases(rawText, TYPE_ALIASES);
  const { specifics } = mapScrapedData(detectedCanonicals, TYPE_ALIASES);
  return specifics;
};

const detectGradesFromProduct = (product) => {
  const text = `${product?.productTitle || ''} ${product?.descriptionText || product?.descriptionHtml || product?.description || ''}`.toLowerCase();
  const found = new Set();
  const rangeRegex = /\b(grades?|gr\.?)\s*(prek|pre-k|pre k|k|\d{1,2})\s*[-–—&]\s*(prek|pre-k|pre k|k|\d{1,2})\b/ig;
  let rangeMatch;
  while ((rangeMatch = rangeRegex.exec(text)) !== null) {
    const a = rangeMatch[2]?.replace(/\s+/g, ''); const b = rangeMatch[3]?.replace(/\s+/g, '');
    const toNum = (t) => t?.startsWith('pre') ? -1 : (t === 'k' ? 0 : parseInt(t, 10));
    const toTag = (n) => n === -1 ? 'PreK' : (n === 0 ? 'K' : String(n));
    const start = toNum(a); const end = toNum(b);
    if (Number.isFinite(start) && Number.isFinite(end)) {
      const lo = Math.min(start, end); const hi = Math.max(start, end);
      for (let n = lo; n <= hi; n++) found.add(toTag(n));
    }
  }
  gradeTokens.forEach(({ canonical, patterns }) => {
    for (const pattern of patterns) {
      if (typeof pattern === 'string') {
        const boundaryRegex = new RegExp(`\\b${escapeRegExp(pattern)}\\b`, 'i');
        if (boundaryRegex.test(text)) found.add(canonical);
      } else if (pattern instanceof RegExp) {
        if (pattern.test(text)) found.add(canonical);
      }
    }
  });
  if (/\b(middle\s*school|junior\s*high)\b/i.test(text)) found.add('Middle School');
  if (/\b(high\s*school|secondary\s*education)\b/i.test(text)) found.add('High School');
  return Array.from(found).sort((a, b) => {
    const gradeOrder = [...GRADE_OPTIONS];
    return gradeOrder.indexOf(a) - gradeOrder.indexOf(b);
  });
};


/* ===================== Upcoming Season Logic ===================== */
const daysUntil = (target) => {
  const now = new Date();
  const y = now.getFullYear();
  let next = new Date(y, target.month, target.day);
  if (next < now) next = new Date(y + 1, target.month, target.day);
  const diff = Math.ceil((next - now) / (1000 * 60 * 60 * 24));
  return { days: diff, date: next };
};

// next 4
const getNextSeasonalCues = () => {
  const candidates = UPCOMING_DATES.map(d => ({ ...d, ...daysUntil(d) }));
  candidates.sort((a,b) => a.days - b.days);
  return candidates.slice(0, 4);
};

const getHealthStatus = (score) => {
  if (score >= 80) return { label: 'Good', tone: 'green' };
  if (score >= 50) return { label: 'Needs Improvement', tone: 'amber' };
  return { label: 'Urgent', tone: 'red' };
};

function useStickyRef(topPx = 96) {
  const ref = useRef(null);
  useEffect(() => { if (!ref.current) return; ref.current.style.top = `${topPx}px`; }, [topPx]);
  return ref;
}

const getProactiveInsight = (product) => {
  const { audit, seoKpis, _seasonalRefs = [], _health, allKeywords, avgRankChange } = product;
  const lowEffort = (audit?.items || []).some(i =>
    !i.pass && ['title_length', 'title_keywords', 'desc_length', 'bullets', 'readability', 'cta'].includes(i.key)
  );
  if (_health?.label === 'Urgent' && (avgRankChange ?? 0) < -3) {
    return { icon: AlertCircle, tone: 'red', text: `**Urgent Action Needed:** Score is low AND average rank dropped significantly (${avgRankChange}). Fix Title, Intro, and CTA first.` };
  }
  if (!audit?.items?.find(i => i.key === 'readability')?.pass && audit?.score < 60) {
    return { icon: Lightbulb, tone: 'amber', text: `**Readability Issue:** Shorten sentences, add bullet points to improve clarity.` };
  }
  if (!audit?.items?.find(i => i.key === 'cta')?.pass && audit?.score < 60) {
    return { icon: Lightbulb, tone: 'amber', text: `**Missing Call to Action:** Add “Download Today”, “Add to Cart”, etc.` };
  }
  if (_seasonalRefs.length > 0) {
    const pretty = _seasonalRefs.slice(0, 3).join(', ') + (_seasonalRefs.length > 3 ? '…' : '');
    return { icon: Calendar, tone: 'purple', text: `**Seasonal Opportunity:** Mentions ${pretty}. Update covers/keywords.` };
  }
  if (seoKpis?.newTop20 > 0) {
    return { icon: Award, tone: 'green', text: `**Momentum:** ${seoKpis.newTop20} new keyword(s) in Top 20. Promote this listing.` };
  }
  if (audit?.score > 75 && (allKeywords?.length || 0) < 3) {
    return { icon: Target, tone: 'blue', text: `**Expand Reach:** Good score. Track more relevant keywords to uncover growth.` };
  }
  if (_health?.label === 'Urgent' && lowEffort) {
    return { icon: Lightbulb, tone: 'amber', text: `**Quick Wins:** Title, Bullets, or CTA appear fixable for fast gains.` };
  }
  if ((avgRankChange ?? 0) < -1) {
    return { icon: ArrowDown, tone: 'red', text: `**Performance Dip:** Average rank dropped (${avgRankChange}). Review failing checks.` };
  }
  return { icon: Star, tone: 'blue', text: `**Solid:** Keep tracking and add new keywords for reach.` };
};

const getConsolidatedTags = (product) => {
  const tags = [];
  const { audit, seoKpis, lastUpdated, allKeywords, _seasonalRefs = [], tptUploadedAt } = product;

  if (_seasonalRefs.length > 0) tags.push({ key: 'seasonal', text: `Seasonal`, tone: 'purple' });

  const contentIssues = ['title_length', 'title_keywords', 'desc_length', 'bullets', 'readability', 'cta'];
  if ((audit?.items || []).some(item => !item.pass && contentIssues.includes(item.key))) {
    tags.push({ key: 'content', text: 'Content Issues', tone: 'amber' });
  }

  if ((seoKpis?.worsened ?? 0) > (seoKpis?.improved ?? 0)) {
    tags.push({ key: 'seo_down', text: 'Trending Down', tone: 'red' });
  } else if ((seoKpis?.improved ?? 0) > 0) {
    tags.push({ key: 'seo_up', text: 'Trending Up', tone: 'green' });
  }

  const refreshThreshold = new Date();
  refreshThreshold.setDate(refreshThreshold.getDate() - 180);

  const lastUpdatedDate = lastUpdated?.toDate ? lastUpdated.toDate() : (lastUpdated ? new Date(lastUpdated) : new Date(0));
  const tptUploadedDate = tptUploadedAt?.toDate ? tptUploadedAt.toDate() : (tptUploadedAt ? new Date(tptUploadedAt) : new Date(0));
  const effectiveLastUpdate = Math.max(lastUpdatedDate.getTime(), tptUploadedDate.getTime());

  if (effectiveLastUpdate < refreshThreshold.getTime() && effectiveLastUpdate > 0) {
    tags.push({ key: 'needs_refresh', text: 'Needs Refresh', tone: 'slate' });
  }

  if (audit?.score >= 75 && (allKeywords?.length || 0) < 3) {
    tags.push({ key: 'high_potential', text: 'High Potential', tone: 'blue' });
  }

  return tags;
};


/* ===================== Filter Bar ===================== */
const FilterBar = ({ filters, setFilters }) => {
  const { search, health, issue, sortBy, improvementLevel } = filters;
  const handle = (k, v) => setFilters(prev => ({ ...prev, [k]: v }));
  const selectClasses = "w-full border-slate-300 rounded-md shadow-sm text-sm focus:ring-sky-500 focus:border-sky-500";

  return (
    <div className="space-y-4" style={{fontFamily: 'inherit'}}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <input
          type="text"
          placeholder="Search products, tags, or IDs..."
          className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-sky-500"
          style={{fontSize: '0.875rem', fontFamily: 'inherit'}}
          value={search}
          onChange={(e) => handle('search', e.target.value)}
        />
      </div>

      <h5 className="text-xs font-bold uppercase tracking-wider text-slate-700 pt-2 border-t border-slate-200 flex items-center gap-1">
        <ClipboardList size={14} /> Sort By
      </h5>
      <select value={sortBy} onChange={e => handle('sortBy', e.target.value)} className={selectClasses} style={{fontFamily: 'inherit', fontSize: '0.875rem', padding: '0.5rem 0.75rem'}}>
        <option value="productTitle_asc">Title (A→Z)</option>
        <option value="productTitle_desc">Title (Z→A)</option>
        <option value="score_desc">Score (High→Low)</option>
        <option value="score_asc">Score (Low→High)</option>
        <option value="updated_desc">Updated (Newest)</option>
        <option value="updated_asc">Updated (Oldest)</option>
      </select>

      <h5 className="text-xs font-bold uppercase tracking-wider text-slate-700 pt-2 border-t border-slate-200 flex items-center gap-1">
        <SlidersHorizontal size={14} /> Health
      </h5>
      <select value={health} onChange={e => handle('health', e.target.value)} className={selectClasses} style={{fontFamily: 'inherit', fontSize: '0.875rem', padding: '0.5rem 0.75rem'}}>
        <option value="all">All Health</option>
        <option value="Good">Good (80+)</option>
        <option value="Needs Improvement">Needs Improvement (50-79)</option>
        <option value="Urgent">Urgent (0-49)</option>
      </select>

      <select value={issue} onChange={e => handle('issue', e.target.value)} className={selectClasses} style={{fontFamily: 'inherit', fontSize: '0.875rem', padding: '0.5rem 0.75rem'}}>
        <option value="all">All Issue Types</option>
        <option value="content">Content Issues</option>
        <option value="seo_down">Trending Down</option>
        <option value="seo_up">Trending Up</option>
        <option value="seasonal">Seasonal</option>
        <option value="needs_refresh">Needs Refresh</option>
        <option value="high_potential">High Potential</option>
      </select>

      <select value={improvementLevel} onChange={e => handle('improvementLevel', e.target.value)} className={selectClasses} style={{fontFamily: 'inherit', fontSize: '0.875rem', padding: '0.5rem 0.75rem'}}>
        <option value="all">All Improvement Levels</option>
        <option value="quick">Quick Wins</option>
        <option value="needs">Needs Work (50–79)</option>
        <option value="strong">Strong (80+)</option>
      </select>
    </div>
  );
};


/* ---------------- Floating Filter Panel ---------------- */
const FloatingFilterPanel = ({
  filters, setFilters, allUniqueTags,
  onReanalyzeAll, isReanalyzingAll, isDemoMode, triggerAuthModal,
  isSortDirty, onRefreshSort, // kept for compatibility
  isBackfilling, onBackfill
}) => {
  const [isTagsExpanded, setIsTagsExpanded] = useState(true);

  const gradeOptions = useMemo(() => GRADE_OPTIONS.map(g => ({ value: g, label: g })), []);
  const seasonalOptions = useMemo(() => SEASON_CANONICALS.map(s => ({ value: s, label: s })).sort((a,b) => a.label.localeCompare(b.label)), []);
  const subjectAreaOptions = useMemo(() =>
    SUBJECT_AREAS.map(s => ({ value: s, label: s })).sort((a,b) => a.label.localeCompare(b.label)), []);
  const allSpecificSubjectOptions = useMemo(() =>
    Array.from(new Set(SUBJECT_ALIASES.map(s => s.canonical))).sort().map(s => ({ value: s, label: s })), []);
  const specificSubjectOptions = useMemo(() => {
    if (filters.subjectArea.length === 0) return allSpecificSubjectOptions;
    const allowedAreas = new Set(filters.subjectArea);
    const specificSubjectsInArea = SUBJECT_ALIASES
      .filter(entry => allowedAreas.has(entry.subjectArea))
      .map(entry => entry.canonical);

    return Array.from(new Set(specificSubjectsInArea)).sort().map(s => ({ value: s, label: s }));
  }, [filters.subjectArea, allSpecificSubjectOptions]);

  const typeOptions = useMemo(() => TYPE_ALIASES.map(t => ({ value: t.canonical, label: t.canonical })).sort((a,b) => a.label.localeCompare(b.label)), []);
  const customTagOptions = useMemo(() => allUniqueTags.map(t => ({ value: t, label: t })), [allUniqueTags]);

  const opportunityOptions = [
    { value: 'all', label: 'All Opportunities' },
    { value: 'untapped', label: 'Untapped Potential (High Score, Low Keywords)'}
  ];
  const activityOptions = [
    { value: 'all', label: 'All Activity' },
    { value: 'rewritten', label: 'Rewritten (Last 90d)' },
    { value: 'uploaded', label: 'Uploaded (Last 90d)' },
    { value: 'untracked', label: 'Products Not Ranking Tracked' },
    { value: 'unscraped', label: 'Products without TPT Tags' },
  ];

  const handleMultiChange = (key, v) => {
    const values = v ? v.map(opt => opt.value) : [];
    setFilters(prev => ({ ...prev, [key]: values }));
  };
  const handleSingleChange = (key, v) => {
    setFilters(prev => ({ ...prev, [key]: v ? v.value : 'all' }));
  };
  const handleSubjectAreaChange = (v) => {
    const values = v ? v.map(opt => opt.value) : [];
    setFilters(prev => ({
      ...prev,
      subjectArea: values,
      specificSubject: values.length === 0 ? [] : prev.specificSubject
    }));
  };

  const selectStyles = {
    control: (base, state) => ({ ...base, backgroundColor: 'white', borderColor: state.isFocused ? '#0ea5e9' : '#cbd5e1', borderRadius: '0.375rem', boxShadow: state.isFocused ? '0 0 0 1px #0ea5e9' : '0 1px 2px 0 rgb(0 0 0 / 0.05)', fontSize: '0.875rem', fontFamily: 'inherit', color: '#1e293b', '&:hover': { borderColor: state.isFocused ? '#0ea5e9' : '#94a3b8' } }),
    option: (base, state) => ({ ...base, backgroundColor: state.isSelected ? '#0ea5e9' : state.isFocused ? '#f0f9ff' : 'white', color: state.isSelected ? 'white' : '#1e293b', fontSize: '0.875rem', fontFamily: 'inherit' }),
    menu: (base) => ({ ...base, zIndex: 20, fontFamily: 'inherit' }),
    multiValue: (base) => ({ ...base, backgroundColor: '#e0f2fe', fontFamily: 'inherit' }),
    multiValueLabel: (base) => ({ ...base, color: '#0369a1', fontSize: '0.875rem', fontFamily: 'inherit' }),
    multiValueRemove: (base) => ({ ...base, color: '#0369a1', '&:hover': { backgroundColor: '#bae6fd', color: '#0c4a6e' } }),
    placeholder: (base) => ({ ...base, color: '#94a3b8', fontSize: '0.875rem', fontFamily: 'inherit' }),
    groupHeading: (base) => ({ ...base, fontSize: '0.7rem', fontWeight: 'bold', textTransform: 'uppercase', color: '#64748b', fontFamily: 'inherit' })
  };

  const stickyRef = useStickyRef(96);

  return (
    <div ref={stickyRef} className="lg:sticky top-24 z-10">
      <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-lg space-y-4 lg:max-h-[calc(100vh-8rem)] lg:overflow-auto" style={{fontFamily: 'inherit'}}>
        <FilterBar filters={filters} setFilters={setFilters} allUniqueTags={allUniqueTags} />

        <div className="pt-4 border-t border-slate-200 space-y-3">
          <button
            className="flex justify-between items-center w-full text-slate-700 font-bold"
            onClick={() => setIsTagsExpanded(s => !s)}
          >
            <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1">
              <Tag size={14} /> Categorical Filters
            </span>
            <ChevronDown size={16} className={`transition-transform ${isTagsExpanded ? 'rotate-180' : ''}`} />
          </button>

          {isTagsExpanded && (
            <div className="space-y-4 pt-2">
              <h5 className="text-xs font-bold uppercase tracking-wider text-slate-500">Opportunity</h5>
              <Select
                options={opportunityOptions}
                styles={selectStyles}
                value={opportunityOptions.find(opt => opt.value === filters.opportunity)}
                onChange={(v) => handleSingleChange('opportunity', v)}
              />

              <h5 className="text-xs font-bold uppercase tracking-wider text-slate-500">Activity</h5>
              <Select
                options={activityOptions}
                styles={selectStyles}
                value={activityOptions.find(opt => opt.value === filters.activity)}
                onChange={(v) => handleSingleChange('activity', v)}
              />

              <h5 className="text-xs font-bold uppercase tracking-wider text-slate-500">Grade Level</h5>
              <Select
                isMulti
                options={gradeOptions}
                styles={selectStyles}
                placeholder="Filter by grade..."
                value={gradeOptions.filter(opt => filters.grade.includes(opt.value))}
                onChange={(v) => handleMultiChange('grade', v)}
              />

              <h5 className="text-xs font-bold uppercase tracking-wider text-slate-500">Subject Area</h5>
              <Select
                isMulti
                options={subjectAreaOptions}
                styles={selectStyles}
                placeholder="Filter by subject area..."
                value={subjectAreaOptions.filter(opt => filters.subjectArea.includes(opt.value))}
                onChange={handleSubjectAreaChange}
              />

              <h5 className="text-xs font-bold uppercase tracking-wider text-slate-500">Specific Subject</h5>
              <Select
                isMulti
                options={specificSubjectOptions}
                styles={selectStyles}
                placeholder="Filter by specific subject..."
                value={specificSubjectOptions.filter(opt => filters.specificSubject.includes(opt.value))}
                onChange={(v) => handleMultiChange('specificSubject', v)}
              />

              <h5 className="text-xs font-bold uppercase tracking-wider text-slate-500">Seasonal Theme</h5>
              <Select
                isMulti
                options={seasonalOptions}
                styles={selectStyles}
                placeholder="Filter by season..."
                value={seasonalOptions.filter(opt => filters.seasonal.includes(opt.value))}
                onChange={(v) => handleMultiChange('seasonal', v)}
              />

              <h5 className="text-xs font-bold uppercase tracking-wider text-slate-500">Resource Type</h5>
              <Select
                isMulti
                options={typeOptions}
                styles={selectStyles}
                placeholder="Filter by type..."
                value={typeOptions.filter(opt => filters.rtype.includes(opt.value))}
                onChange={(v) => handleMultiChange('rtype', v)}
              />

              <h5 className="text-xs font-bold uppercase tracking-wider text-slate-500">Custom Tag</h5>
              <Select
                isMulti
                options={customTagOptions}
                styles={selectStyles}
                placeholder="Filter by custom tag..."
                isDisabled={allUniqueTags.length === 0}
                value={customTagOptions.filter(opt => filters.customTag.includes(opt.value))}
                onChange={(v) => handleMultiChange('customTag', v)}
              />
            </div>
          )}
        </div>

        {/*<div className="pt-4 border-t border-slate-200 space-y-2">*/}
        {/*  <button*/}
        {/*    className="w-full inline-flex items-center justify-center gap-2 font-bold px-4 py-2 rounded-lg transition-colors bg-sky-600 hover:bg-sky-700 text-white disabled:bg-slate-400"*/}
        {/*    disabled={isDemoMode || isReanalyzingAll || isBackfilling}*/}
        {/*    onClick={() => (isDemoMode ? triggerAuthModal?.('Sign up to enable!') : onReanalyzeAll?.())}*/}
        {/*    title="Re-run backend audit for all products"*/}
        {/*  >*/}
        {/*    <RefreshCw size={16} className={isReanalyzingAll ? 'animate-spin' : ''} />*/}
        {/*    {isReanalyzingAll ? 'Updating All Audits...' : 'Update All Audits'}*/}
        {/*  </button>*/}

        {/*  <button*/}
        {/*    className="w-full inline-flex items-center justify-center gap-2 font-bold px-4 py-2 rounded-lg transition-colors border border-sky-600 text-sky-600 hover:bg-sky-50 disabled:bg-slate-300 disabled:text-slate-500"*/}
        {/*    disabled={isDemoMode || isBackfilling || isReanalyzingAll}*/}
        {/*    onClick={() => (isDemoMode ? triggerAuthModal?.('Sign up to enable!') : onBackfill?.())}*/}
        {/*    title="Refresh old products with the latest structured data (Grades, Subjects, etc.) from TPT. This runs in batches."*/}
        {/*  >*/}
        {/*    <DatabaseZap size={16} className={isBackfilling ? 'animate-spin' : ''} />*/}
        {/*    {isBackfilling ? 'Refreshing Old Data...' : 'Refresh Old Product Data'}*/}
        {/*  </button>*/}
        {/*</div>*/}
      </div>
    </div>
  );
};


/* ---------------- Main Component ---------------- */
export default function ProductImprovementHub({
  currentUser,
  hubProducts, userTptProducts, onGoToSeoTracker,
  onReanalyzeAll,
  onUpdatePins, onGoToSeoAndSelectKeyword,
  onUpdateSeasonalTags, onUpdateSubjectTags, onUpdateGradeTags, onUpdateResourceTypes,
  onUpdateCustomTags, onMarkProductUpdated, onMarkUploadedToTpt,
  onUpdateAuditOverrides,
  isReanalyzingAll = false, isDemoMode = false, triggerAuthModal,
  onGoToRewrite,
  // NEW: focus support
  focusProductId,
  onClearFocus
}) {

  const [filters, setFilters] = useState({
    search: '', health: 'all', issue: 'all', sortBy: 'productTitle_asc',
    customTag: [],
    seasonal: [],
    subjectArea: [],
    specificSubject: [],
    rtype: [],
    grade: [],
    improvementLevel: 'all',
    opportunity: 'all',
    activity: 'all',
  });

  const [isGraphModalOpen, setIsGraphModalOpen] = useState(false);
  const [graphModalData, setGraphModalData] = useState(null);
  const [isGraphLoading, setIsGraphLoading] = useState(false);
  const [displayProducts, setDisplayProducts] = useState([]);

  const productRowRefs = useRef(new Map());
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [refreshingProductId, setRefreshingProductId] = useState(null);

  const nextFourSeasons = getNextSeasonalCues();

  // Build normalized list
  const normalized = useMemo(() => {
    const rawProducts = (hubProducts && hubProducts.length ? hubProducts : (userTptProducts || []));

    return rawProducts.map((p) => {
      const overrides = (p.auditOverrides ?? p.auditOverride ?? {});

      const keywordSummariesArr = Array.isArray(p.keywordSummaries)
        ? p.keywordSummaries
        : (p.keywordSummaries && typeof p.keywordSummaries === 'object'
            ? Object.values(p.keywordSummaries)
            : []);

      const allKeywords = keywordSummariesArr.map(summary => ({
        id: summary.id,
        keyword: summary.keyword || summary.id,
        curr: summary.currentRank,
        prev: summary.previousRank,
        delta: (summary.previousRank != null && summary.currentRank != null) ? summary.previousRank - summary.currentRank : null,
      }));

      const audit = runAudit(p, overrides);
      const health = getHealthStatus(audit.score);
      const lastUpdated = p.lastUpdated || null;
      const tptUploadedAt = p.tptUploadedAt || null;

      let gradeRefs, subjectAreaTags, specificSubjectTags, typeRefs;

      if (p.scrapedGrades && p.scrapedGrades.length > 0) gradeRefs = p.scrapedGrades;
      else gradeRefs = detectGradesFromProduct(p);

      if (p.scrapedSubjects && p.scrapedSubjects.length > 0) {
        const { areas, specifics } = mapScrapedData(p.scrapedSubjects, SUBJECT_ALIASES);
        subjectAreaTags = areas; specificSubjectTags = specifics;
      } else {
        const { areas, specifics } = detectSubjectsFromProduct(p);
        subjectAreaTags = areas; specificSubjectTags = specifics;
      }

      if (p.scrapedTypes && p.scrapedTypes.length > 0) {
        const { specifics } = mapScrapedData(p.scrapedTypes, TYPE_ALIASES);
        typeRefs = specifics;
      } else {
        typeRefs = detectTypesFromProduct(p);
      }

      const scrapedSeasonal = mapScrapedData(p.scrapedTypes || [], SEASON_ALIASES).specifics;
      const detectedSeasonal = detectSeasonsFromProduct(p);
      const seasonalRefs = Array.from(new Set([...scrapedSeasonal, ...detectedSeasonal]));

      const productForAnalysis = {
        ...p, audit,
        _seasonalRefs: seasonalRefs,
        _subjectAreaTags: subjectAreaTags,
        _specificSubjectTags: specificSubjectTags,
        _gradeRefs: gradeRefs,
        _typeRefs: typeRefs,
        _health: health, allKeywords, seoKpis: p.seoKpis, lastUpdated,
        avgRankChange: p.avgRankChange, auditOverrides: overrides, tptUploadedAt,
        gradeTags: p.gradeTags || [],
      };

      const tags = getConsolidatedTags(productForAnalysis);
      const insight = getProactiveInsight(productForAnalysis);

      return {
        ...productForAnalysis,
        id: p.id,
        productTitle: p.productTitle || p.title || 'Untitled',
        _tags: tags, _insight: insight, customTags: p.customTags || [],
        seasonalTags: p.seasonalTags || [], subjectTags: p.subjectTags || [],
        resourceTypeTags: p.resourceTypeTags || [], pinnedKeywords: p.pinnedKeywords || [],
      };
    });
  }, [hubProducts, userTptProducts]);

  useEffect(() => { setDisplayProducts(normalized); }, [normalized]);

  const allUniqueTags = useMemo(() => {
    const tagsSet = new Set();
    displayProducts.forEach(p => { (p.customTags || []).forEach(tag => tagsSet.add(tag)); });
    return Array.from(tagsSet).sort();
  }, [displayProducts]);

  const filteredProducts = useMemo(() => {
    let base = [...displayProducts];
    const { search, health, improvementLevel, seasonal, subjectArea, specificSubject, rtype, grade, issue, customTag, opportunity, activity } = filters;
    const nextSeasonName = nextFourSeasons[0]?.name.toLowerCase();
    const ninetyDaysAgo = new Date(new Date().setDate(new Date().getDate() - 90)).getTime();

    if (search) {
      const q = normalize(search);
      base = base.filter(p =>
        normalize(p.productTitle || '').includes(q) ||
        (p.customTags || []).some(tag => normalize(tag).includes(q)) ||
        (p._specificSubjectTags || []).some(tag => normalize(tag).includes(q)) ||
        (p._subjectAreaTags || []).some(tag => normalize(tag).includes(q))
      );
    }
    if (health !== 'all') base = base.filter(p => p._health.label === health);

    if (improvementLevel !== 'all') {
      base = base.filter(p => {
        const lowEffort = (p.audit?.items || []).some(i =>
          !i.pass && ['title_length', 'title_keywords', 'desc_length', 'bullets', 'readability', 'cta'].includes(i.key)
        );
        if (improvementLevel === 'quick') return lowEffort;
        if (improvementLevel === 'needs') return p.audit?.score >= 50 && p.audit?.score < 80;
        if (improvementLevel === 'strong') return p.audit?.score >= 80;
        return true;
      });
    }

    if (opportunity === 'untapped') {
      base = base.filter(p => (p.audit?.score || 0) >= 80 && (p.allKeywords?.length || 0) < 3);
    }

    if (activity === 'rewritten') {
      base = base.filter(p => (p.lastUpdated?.toDate ? p.lastUpdated.toDate().getTime() : new Date(p.lastUpdated).getTime() || 0) > ninetyDaysAgo);
    } else if (activity === 'uploaded') {
      base = base.filter(p => (p.tptUploadedAt?.toDate ? p.tptUploadedAt.toDate().getTime() : new Date(p.tptUploadedAt).getTime() || 0) > ninetyDaysAgo);
    } else if (activity === 'untracked') {
      base = base.filter(p => (p.allKeywords?.length || 0) === 0);
    } else if (activity === 'unscraped') {
      base = base.filter(p => (!p.scrapedGrades?.length && !p.scrapedSubjects?.length));
    }

    if (issue !== 'all') base = base.filter(p => p._tags.some(t => t.key === issue));

    if (grade.length > 0) base = base.filter(p =>
      grade.every(g => (p.gradeTags || []).includes(g) || (p._gradeRefs || []).includes(g))
    );

    if (seasonal.length > 0) {
      if (seasonal.includes('none') && nextSeasonName) {
        base = base.filter(p => {
          const hasSeasonalTag =
            (p.seasonalTags || []).some(tag => tag.toLowerCase() === nextSeasonName) ||
            (p._seasonalRefs || []).some(tag => tag.toLowerCase() === nextSeasonName);
          return !hasSeasonalTag;
        });
      } else {
        base = base.filter(p =>
          seasonal.every(s => (p.seasonalTags || []).includes(s) || (p._seasonalRefs || []).includes(s))
        );
      }
    }

    if (subjectArea.length > 0)
      base = base.filter(p => subjectArea.every(s => (p.subjectTags || []).includes(s) || (p._subjectAreaTags || []).includes(s)));
    if (specificSubject.length > 0)
      base = base.filter(p => specificSubject.every(s => (p.subjectTags || []).includes(s) || (p._specificSubjectTags || []).includes(s)));
    if (rtype.length > 0)
      base = base.filter(p => rtype.every(t => (p.resourceTypeTags || []).includes(t) || (p._typeRefs || []).includes(t)));
    if (customTag.length > 0)
      base = base.filter(p => customTag.every(t => (p.customTags || []).includes(t)));

    return base;
  }, [displayProducts, filters, nextFourSeasons]);

  // Sorted list
  const sortedProducts = useMemo(() => {
    const { sortBy } = filters;
    const listToSort = [...filteredProducts];

    listToSort.sort((a, b) => {
      const scoreA = a.audit?.score ?? 0;
      const scoreB = b.audit?.score ?? 0;
      const titleA = a.productTitle || '';
      const titleB = b.productTitle || '';
      const dateA = a.lastUpdated?.toDate ? a.lastUpdated.toDate() : (a.lastUpdated ? new Date(a.lastUpdated) : new Date(0));
      const dateB = b.lastUpdated?.toDate ? b.lastUpdated.toDate() : (b.lastUpdated ? new Date(b.lastUpdated) : new Date(0));
      switch (sortBy) {
        case 'score_asc': return scoreA - scoreB;
        case 'score_desc': return scoreB - scoreA;
        case 'updated_desc': return dateB - dateA;
        case 'updated_asc': return dateA - dateB;
        case 'productTitle_asc': return titleA.localeCompare(titleB);
        case 'productTitle_desc': return titleB.localeCompare(titleA);
        default: return 0;
      }
    });

    return listToSort;

  }, [filteredProducts, filters.sortBy]);

  // paging & selection
  const [pageCount, setPageCount] = useState(1);
  useEffect(() => { setPageCount(1); }, [filters]); // Reset page count on filter change
  const pageSize = 20;
  const [selected, setSelected] = useState(null);
  const handleSelect = (product) => setSelected(prev => (prev?.id === product.id ? null : product));

  useEffect(() => {
    if (selected) {
      const isStillVisible = sortedProducts.some(p => p.id === selected.id);
      if (!isStillVisible) setSelected(null);
      else {
        const updatedData = sortedProducts.find(p => p.id === selected.id);
        if (updatedData && updatedData !== selected) setSelected(updatedData);
      }
    }
  }, [sortedProducts, selected]);

  const avgAuditScore = useMemo(
    () => displayProducts.length
      ? Math.round(displayProducts.reduce((s, p) => s + (p.audit?.score || 0), 0) / displayProducts.length)
      : 0,
    [displayProducts]
  );

  const handleQuickFix = useCallback((issueKey, product) => { onGoToRewrite?.(product); }, [onGoToRewrite]);

  const handleLocalOverrideUpdate = useCallback(async (productId, patch) => {
    let updatedProductForDisplay;

    setDisplayProducts(prev =>
      prev.map(p => {
        if (p.id !== productId) return p;

        const newOverrides = { ...(p.auditOverrides || {}) };
        const [key] = Object.keys(patch);
        const value = patch[key];

        if (value === true) {
          newOverrides[key] = true;
        } else {
          delete newOverrides[key];
        }

        const temp = { ...p, auditOverrides: newOverrides };
        const newAudit = runAudit(temp, newOverrides);
        const productForAnalysis = { ...temp, audit: newAudit, _health: getHealthStatus(newAudit.score) };

        updatedProductForDisplay = {
          ...p,
          auditOverrides: newOverrides,
          audit: newAudit,
          _tags: getConsolidatedTags(productForAnalysis),
          _insight: getProactiveInsight(productForAnalysis),
          _health: getHealthStatus(newAudit.score),
        };
        return updatedProductForDisplay;
      })
    );

    if (updatedProductForDisplay) {
      setSelected(prevSel => prevSel?.id === productId ? updatedProductForDisplay : prevSel);
    }

  }, []);

  const openGraphModal = useCallback(async (product, keyword) => {
    if (!currentUser || !product || !keyword) { alert("Cannot load graph: missing user or product data."); return; }
    const summary = product.allKeywords?.find(s => s.keyword === keyword);
    if (!summary || !summary.id) { alert("Could not find keyword summary ID."); return; }
    setIsGraphLoading(true); setIsGraphModalOpen(true);
    setGraphModalData({ keyword, history: [] });
    try {
      const historyQuery = query(
        collection(db, `users/${currentUser.uid}/products/${product.id}/keywordSummaries/${summary.id}/history`),
        orderBy("date", "asc")
      );
      const historySnapshot = await getDocs(historyQuery);
      const history = historySnapshot.docs.map(doc => ({ ...doc.data(), date: doc.data().date.toMillis() }));
      setGraphModalData({ keyword, history });
    } catch(e) {
      console.error("Failed to fetch graph history", e);
      alert("Could not load rank history.");
      setIsGraphModalOpen(false);
    } finally {
      setIsGraphLoading(false);
    }
  }, [currentUser]);

  const handleBackfill = useCallback(async () => {
    if (!currentUser) { triggerAuthModal?.('Please log in to refresh your products.'); return; }
    if (!window.confirm("This will refresh data for up to 50 of your oldest products. This will re-scrape TPT for structured tags (it will NOT overwrite your title or description). Are you sure?")) { return; }
    setIsBackfilling(true);
    try {
      const backfillFunction = httpsCallable(functionsInstance, 'backfillMissingDescriptions');
      const result = await backfillFunction();
      const { attempted, updated } = result.data;
      alert(`Refresh complete! Attempted: ${attempted}, Products Updated: ${updated}. Your product list will now refresh with the new tags.`);
    } catch (error) {
      console.error("Error running backfill:", error);
      alert(`An error occurred: ${error.message}`);
    } finally {
      setIsBackfilling(false);
    }
  }, [currentUser, triggerAuthModal]);

  const handleRefreshSingle = useCallback(async (productId) => {
    if (!currentUser) { triggerAuthModal?.('Please log in to refresh tags.'); return; }
    setRefreshingProductId(productId);
    try {
      const refreshFunction = httpsCallable(functionsInstance, 'refreshSingleProductTags');
      await refreshFunction({ productId });
    } catch (error) {
      console.error("Error refreshing single product tags:", error);
      alert(`An error occurred while refreshing tags: ${error.message}`);
    } finally {
      setRefreshingProductId(null);
    }
  }, [currentUser, triggerAuthModal]);

  // Focus helpers for Headliners quick filters
  const resetFilters = () => ({
    search: '',
    health: 'all', opportunity: 'all', seasonal: [], issue: 'all',
    subjectArea: [], specificSubject: [], grade: [], rtype: [],
    customTag: [], activity: 'all', improvementLevel: 'all'
  });

  const handleFocusHealth = (level) => {
    setFilters(f => ({ ...f, ...resetFilters(), health: level }));
    document.getElementById("product-hub-top")?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const handleFocusSeasonal = (season) => {
    const seasonToFilter = season === 'none' ? ['none'] : [season];
    setFilters(f => ({ ...f, ...resetFilters(), seasonal: seasonToFilter }));
    document.getElementById("product-hub-top")?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const handleFocusOpportunity = (type) => {
    setFilters(f => ({ ...f, ...resetFilters(), opportunity: type }));
    document.getElementById("product-hub-top")?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const handleFocusIssue = (type) => {
    setFilters(f => ({ ...f, ...resetFilters(), issue: type }));
    document.getElementById("product-hub-top")?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const handleFocusActivity = (type) => {
    setFilters(f => ({ ...f, ...resetFilters(), activity: type }));
    document.getElementById("product-hub-top")?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const handleFocusTracking = (type) => {
    setFilters(f => ({ ...f, ...resetFilters(), activity: type }));
    document.getElementById("product-hub-top")?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // --- Listen for global "openCoachForProduct" and jump to item (handles deep-linking) ---
  // This is the deprecated global event listener previously removed to resolve conflicts.
  /*
  useEffect(() => {
    const handler = (e) => {
      const id = e?.detail?.productId;
      if (!id) return;

      const idx = sortedProducts.findIndex(p => p.id === id);
      if (idx < 0) return;

      const neededPages = Math.max(1, Math.ceil((idx + 1) / pageSize));
      setPageCount(p => Math.max(p, neededPages));

      const target = sortedProducts[idx];
      setSelected(target);

      setTimeout(() => {
        const el =
          productRowRefs.current.get(id) ||
          document.getElementById(`coach-item-${id}`) ||
          document.querySelector(`[data-coach-item="${id}"]`) ||
          document.querySelector(`[data-product-id="${id}"]`);

        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-2', 'ring-sky-400', 'ring-offset-2');
          setTimeout(() => {
            el.classList.remove('ring-2', 'ring-sky-400', 'ring-offset-2');
          }, 1500);
        }
      }, 50);
    };

    window.addEventListener('openCoachForProduct', handler);
    return () => window.removeEventListener('openCoachForProduct', handler);
  }, [sortedProducts]);
  */

  // --- NEW: Focus a specific product tile (select, expand, scroll, highlight) ---
  useEffect(() => {
    if (!focusProductId) return;

    const tryFocus = () => {
      const idx = sortedProducts.findIndex(p => p.id === focusProductId);
      if (idx < 0) return false;

      const neededPages = Math.max(1, Math.ceil((idx + 1) / pageSize));
      setPageCount(p => Math.max(p, neededPages));

      const target = sortedProducts[idx];
      setSelected(prev => (prev?.id === target.id ? prev : target));

      // --------------------------------------------------------------------------------
      // FIX: Increased the timeout delay to 200ms to allow the DOM to fully update
      //      after the `setSelected` state change.
      // --------------------------------------------------------------------------------
      setTimeout(() => {
        const el = productRowRefs.current.get(focusProductId)
          || document.getElementById(`coach-item-${focusProductId}`)
          || document.querySelector(`[data-coach-item="${focusProductId}"]`)
          || document.querySelector(`[data-product-id="${focusProductId}"]`);

        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-2', 'ring-sky-400', 'ring-offset-2');
          setTimeout(() => {
            el.classList.remove('ring-2', 'ring-sky-400', 'ring-offset-2');
          }, 1500);
        }
        onClearFocus?.();
      }, 200); // <-- CHANGED DELAY HERE

      return true;
    };

    // Try with current filters first
    if (tryFocus()) return;

    // If filtered out, relax filters and try on next tick
    setFilters(f => ({
      ...f,
      search: '',
      health: 'all',
      issue: 'all',
      seasonal: [],
      subjectArea: [],
      specificSubject: [],
      rtype: [],
      grade: [],
      improvementLevel: 'all',
      opportunity: 'all',
      activity: 'all',
      customTag: [],
    }));

    const t = setTimeout(() => { tryFocus() || onClearFocus?.(); }, 0);
    return () => clearTimeout(t);
  }, [focusProductId, sortedProducts, pageSize, onClearFocus]);
  // ------------------------------------------------------------------------------

  // --- RENDER ---
  return (
    <div className="font-sans" id="product-hub-top">
      <section className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-black tracking-tight text-slate-700">Improvement Hub</h2>
            <p className="text-sm text-slate-500 mt-2">Spot opportunities → fix → grow.</p>
            <p className="text-xs text-slate-400 mt-2 max-w-2xl">
              Welcome to the Hub! This dashboard analyzes your products to find your best growth opportunities.
              <span className="font-semibold text-amber-600"> This tool is in beta, so you may notice a few tweaks as we continue to improve it.</span>
            </p>
          </div>
        </div>
        <div className="mt-6">
          <Headliners
            avgAuditScore={avgAuditScore}
            displayProducts={normalized}
            nextFiveSeasons={nextFourSeasons}
            onFocusHealth={handleFocusHealth}
            onFocusSeasonal={handleFocusSeasonal}
            onFocusOpportunity={handleFocusOpportunity}
            onFocusIssue={handleFocusIssue}
            onFocusActivity={handleFocusActivity}
            onFocusTracking={handleFocusTracking}
            onGoToSeoTracker={onGoToSeoTracker}
            onBackfill={handleBackfill}
            isBackfilling={isBackfilling}
          />
        </div>
      </section>

      <div className="flex flex-col lg:flex-row gap-8">
        <div className="lg:order-1 lg:flex-1 min-w-0">
          <div className="space-y-3">
            {sortedProducts.length ? (
              sortedProducts.slice(0, pageCount * pageSize).map((p) => (
                <React.Fragment key={p.id}>
                  <div
                    id={`coach-item-${p.id}`}
                    ref={(el) => { if (el) productRowRefs.current.set(p.id, el); }}
                  >
                    <ProductRow
                      product={p}
                      selected={selected?.id === p.id}
                      isRefreshing={refreshingProductId === p.id}
                      onSelect={() => handleSelect(p)}
                      onMarkProductUpdated={onMarkProductUpdated}
                      onMarkUploadedToTpt={onMarkUploadedToTpt}
                      onShowGraphPopup={(keyword) => openGraphModal(p, keyword)}
                      onGoToRewrite={() => onGoToRewrite?.(p)}
                      onRefreshTags={() => handleRefreshSingle(p.id)}
                    />
                  </div>
                  {selected?.id === p.id && (
                    <div className="mb-3 -mt-1 rounded-b-xl border-x border-b border-slate-300 overflow-hidden shadow-sm">
                      <CoachPanel
                        product={selected}
                        onGoToSeo={onGoToSeoTracker}
                        isDemoMode={isDemoMode}
                        triggerAuthModal={triggerAuthModal}
                        onQuickFix={handleQuickFix}
                        onUpdatePins={onUpdatePins}
                        onUpdateCustomTags={onUpdateCustomTags}
                        onUpdateSeasonalTags={onUpdateSeasonalTags}
                        onUpdateSubjectTags={onUpdateSubjectTags}
                        onUpdateGradeTags={onUpdateGradeTags}
                        onUpdateResourceTypes={onUpdateResourceTypes}
                        onMarkProductUpdated={onMarkProductUpdated}
                        onMarkUploadedToTpt={onMarkUploadedToTpt}
                        onUpdateAuditOverrides={handleLocalOverrideUpdate} // persists & updates UI

                        // Pass db and user ID to CoachPanel
                        db={db}
                        currentUserId={currentUser?.uid}
                      />
                    </div>
                  )}
                </React.Fragment>
              ))
            ) : (
              <Skeleton />
            )}
            {sortedProducts.length > (pageCount * pageSize) && (
              <div className="flex justify-center pt-4">
                <button
                  onClick={() => setPageCount(p => p + 1)}
                  className="px-4 py-2 rounded-lg border bg-white hover:bg-slate-50 text-slate-700 font-bold text-sm"
                >
                  Load More
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="lg:order-2 lg:w-96 flex-shrink-0">
          <FloatingFilterPanel
            filters={filters}
            setFilters={setFilters}
            allUniqueTags={allUniqueTags}
            onReanalyzeAll={onReanalyzeAll}
            isReanalyzingAll={isReanalyzingAll}
            isDemoMode={isDemoMode}
            triggerAuthModal={triggerAuthModal}

            // kept for compatibility
            isSortDirty={false}
            onRefreshSort={() => {}}

            isBackfilling={isBackfilling}
            onBackfill={handleBackfill}
          />
        </div>
      </div>

      <HubRankHistoryModal
        isOpen={isGraphModalOpen}
        onClose={() => setIsGraphModalOpen(false)}
        historyData={graphModalData?.history}
        keyword={graphModalData?.keyword}
        isLoading={isGraphLoading}
      />
    </div>
  );
}

/* ---------------- Skeleton (Local Component) ---------------- */
const Skeleton = () => (
  <div className="space-y-4">
    {[...Array(3)].map((_, idx) => (
      <div key={`sk-${idx}`} className="p-4 bg-white border border-slate-200 rounded-xl animate-pulse">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-full bg-sky-100" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-slate-100 rounded w-3/4" />
            <div className="h-3 bg-slate-100 rounded w-full" />
            <div className="h-3 bg-slate-100 rounded w-5/6" />
          </div>
        </div>
      </div>
    ))}
  </div>
);